import React, { useEffect, useState, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { apiService } from '../services/api';
import { formatDate, formatDateTime } from '../lib/utils';

interface Transaction {
  id: number;
  transaction_date: string;
  amount: number;
  currency: string;
  reference: string | null;
  description: string | null;
  status: 'unmatched' | 'matched';
  matched_invoice_id: string | null;
  matched_at: string | null;
}

interface CustomerInvoice {
  documentNumber: string;
  customerName: string;
  amount: number;
  balance: number;
  currency: string;
  dueDate: string;
  ocr: string | null;
}

type TabType = 'unmatched' | 'matched';

export function Reconciliation() {
  const [tab, setTab] = useState<TabType>('unmatched');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [invoices, setInvoices] = useState<CustomerInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedTx, setSelectedTx] = useState<number | null>(null);
  const [matching, setMatching] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState({
    transaction_date: new Date().toISOString().slice(0, 10),
    amount: '',
    currency: 'SEK',
    reference: '',
    description: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [txData, invData] = await Promise.all([
        apiService.getTransactions(),
        apiService.getCustomerInvoices(),
      ]);
      setTransactions(txData.transactions);
      setInvoices(invData.invoices);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleMatch = async (invoiceDocumentNumber: string) => {
    if (!selectedTx) return;
    setMatching(true);
    try {
      await apiService.matchTransaction(selectedTx, invoiceDocumentNumber);
      setSelectedTx(null);
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to match');
    } finally {
      setMatching(false);
    }
  };

  const handleUnmatch = async (id: number) => {
    try {
      await apiService.unmatchTransaction(id);
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unmatch');
    }
  };

  const handleAddTransaction = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiService.createTransaction({
        transaction_date: form.transaction_date,
        amount: parseFloat(form.amount),
        currency: form.currency,
        reference: form.reference || undefined,
        description: form.description || undefined,
      });
      setForm({ transaction_date: new Date().toISOString().slice(0, 10), amount: '', currency: 'SEK', reference: '', description: '' });
      setShowAddForm(false);
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add transaction');
    } finally {
      setSubmitting(false);
    }
  };

  const unmatched = transactions.filter((t) => t.status === 'unmatched');
  const matched = transactions.filter((t) => t.status === 'matched');
  const matchedInvoiceIds = new Set(matched.map((t) => t.matched_invoice_id).filter(Boolean));
  const availableInvoices = invoices.filter((inv) => !matchedInvoiceIds.has(inv.documentNumber));
  const selectedTxData = transactions.find((t) => t.id === selectedTx);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Reconciliation</h1>
            <p className="text-sm text-muted-foreground mt-1">Match incoming bank transactions to customer invoices.</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowAddForm((v) => !v)}
              className="h-9 px-4 rounded-md bg-foreground text-background text-sm font-medium transition-opacity hover:opacity-80"
            >
              {showAddForm ? 'Cancel' : '+ Add transaction'}
            </button>
            <button
              onClick={loadAll}
              disabled={loading}
              className="h-9 px-3 rounded-md border border-border/60 bg-white text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
            >
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Add transaction form */}
        {showAddForm && (
          <div className="bg-white border border-border/60 rounded-xl p-5">
            <p className="text-sm font-semibold mb-4">New bank transaction</p>
            <form onSubmit={handleAddTransaction} className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Date</label>
                <input
                  type="date"
                  lang="sv"
                  required
                  value={form.transaction_date}
                  onChange={(e) => setForm((f) => ({ ...f, transaction_date: e.target.value }))}
                  className="w-full h-9 text-sm border border-border/60 rounded-md px-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-foreground/20"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  className="w-full h-9 text-sm border border-border/60 rounded-md px-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-foreground/20"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Currency</label>
                <input
                  type="text"
                  maxLength={3}
                  value={form.currency}
                  onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))}
                  className="w-full h-9 text-sm border border-border/60 rounded-md px-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-foreground/20"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Reference / OCR</label>
                <input
                  type="text"
                  placeholder="Optional"
                  value={form.reference}
                  onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
                  className="w-full h-9 text-sm border border-border/60 rounded-md px-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-foreground/20"
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <label className="text-xs font-medium text-muted-foreground">Description</label>
                <input
                  type="text"
                  placeholder="Optional"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="w-full h-9 text-sm border border-border/60 rounded-md px-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-foreground/20"
                />
              </div>
              <div className="sm:col-span-3 flex justify-end pt-1">
                <button
                  type="submit"
                  disabled={submitting}
                  className="h-9 px-4 rounded-md bg-foreground text-background text-sm font-medium transition-opacity hover:opacity-80 disabled:opacity-40"
                >
                  {submitting ? 'Adding…' : 'Add transaction'}
                </button>
              </div>
            </form>
          </div>
        )}

        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-2.5">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b border-border/60">
          {(['unmatched', 'matched'] as TabType[]).map((t) => {
            const count = t === 'unmatched' ? unmatched.length : matched.length;
            return (
              <button
                key={t}
                onClick={() => { setTab(t); setSelectedTx(null); }}
                className={`px-4 py-2 text-sm font-medium capitalize transition-colors border-b-2 -mb-px flex items-center gap-2 ${
                  tab === t
                    ? 'border-foreground text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {t}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${tab === t ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Unmatched tab — two panel layout */}
        {tab === 'unmatched' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Transactions panel */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1">Bank transactions</p>
              <div className="bg-[#fafafa] border border-border/60 rounded-xl overflow-hidden">
                {loading ? (
                  <p className="p-6 text-sm text-center text-muted-foreground">Loading…</p>
                ) : unmatched.length === 0 ? (
                  <p className="p-6 text-sm text-center text-muted-foreground">No unmatched transactions.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/60 bg-muted/60">
                        <th className="p-3 text-left font-medium text-muted-foreground">Date</th>
                        <th className="p-3 text-left font-medium text-muted-foreground">Reference</th>
                        <th className="p-3 text-right font-medium text-muted-foreground">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unmatched.map((tx) => (
                        <tr
                          key={tx.id}
                          onClick={() => setSelectedTx(selectedTx === tx.id ? null : tx.id)}
                          className={`border-b border-border/40 last:border-0 cursor-pointer transition-colors ${
                            selectedTx === tx.id
                              ? 'bg-foreground text-background'
                              : 'hover:bg-muted/30'
                          }`}
                        >
                          <td className="p-3 text-xs">{formatDate(tx.transaction_date)}</td>
                          <td className="p-3 text-xs">
                            <div>{tx.reference ?? <span className="opacity-40">—</span>}</div>
                            {tx.description && <div className={`text-xs mt-0.5 ${selectedTx === tx.id ? 'opacity-60' : 'text-muted-foreground'}`}>{tx.description}</div>}
                          </td>
                          <td className="p-3 text-right font-mono text-xs">
                            {Number(tx.amount).toLocaleString('sv-SE', { minimumFractionDigits: 2 })} {tx.currency}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Invoices panel */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1">
                Customer invoices
                {selectedTxData && (
                  <span className="ml-2 normal-case font-normal text-foreground">
                    — select one to match with {selectedTxData.amount.toLocaleString('sv-SE', { minimumFractionDigits: 2 })} {selectedTxData.currency}
                  </span>
                )}
              </p>
              <div className="bg-[#fafafa] border border-border/60 rounded-xl overflow-hidden">
                {availableInvoices.length === 0 ? (
                  <p className="p-6 text-sm text-center text-muted-foreground">No open customer invoices.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/60 bg-muted/60">
                        <th className="p-3 text-left font-medium text-muted-foreground">Customer</th>
                        <th className="p-3 text-left font-medium text-muted-foreground">Invoice #</th>
                        <th className="p-3 text-right font-medium text-muted-foreground">Balance</th>
                        <th className="p-3 w-10" />
                      </tr>
                    </thead>
                    <tbody>
                      {availableInvoices.map((inv) => (
                        <tr key={inv.documentNumber} className="border-b border-border/40 last:border-0 hover:bg-muted/30 transition-colors">
                          <td className="p-3 font-medium text-sm">{inv.customerName}</td>
                          <td className="p-3 font-mono text-xs text-muted-foreground">{inv.documentNumber}</td>
                          <td className="p-3 text-right font-mono text-xs">
                            {Number(inv.balance).toLocaleString('sv-SE', { minimumFractionDigits: 2 })} {inv.currency}
                          </td>
                          <td className="p-3">
                            {selectedTx && (
                              <button
                                onClick={() => handleMatch(inv.documentNumber)}
                                disabled={matching}
                                className="text-xs h-7 px-2.5 rounded-md bg-foreground text-background font-medium transition-opacity hover:opacity-80 disabled:opacity-40 whitespace-nowrap"
                              >
                                Match
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Matched tab */}
        {tab === 'matched' && (
          <div className="bg-[#fafafa] border border-border/60 rounded-xl overflow-hidden">
            {matched.length === 0 ? (
              <p className="p-8 text-sm text-center text-muted-foreground">No matched transactions yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 bg-muted/60">
                    <th className="p-3 text-left font-medium text-muted-foreground">Date</th>
                    <th className="p-3 text-left font-medium text-muted-foreground">Reference</th>
                    <th className="p-3 text-right font-medium text-muted-foreground">Amount</th>
                    <th className="p-3 text-left font-medium text-muted-foreground">Matched to</th>
                    <th className="p-3 text-left font-medium text-muted-foreground">Matched at</th>
                    <th className="p-3 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {matched.map((tx) => (
                    <tr key={tx.id} className="border-b border-border/40 last:border-0 hover:bg-muted/20 transition-colors">
                      <td className="p-3 text-xs">{tx.transaction_date}</td>
                      <td className="p-3 text-xs text-muted-foreground">{tx.reference ?? '—'}</td>
                      <td className="p-3 text-right font-mono text-xs">
                        {Number(tx.amount).toLocaleString('sv-SE', { minimumFractionDigits: 2 })} {tx.currency}
                      </td>
                      <td className="p-3 font-mono text-xs text-muted-foreground">{tx.matched_invoice_id}</td>
                      <td className="p-3 text-xs text-muted-foreground">
                        {formatDateTime(tx.matched_at)}
                      </td>
                      <td className="p-3">
                        <button
                          onClick={() => handleUnmatch(tx.id)}
                          className="text-xs text-muted-foreground hover:text-destructive transition-colors"
                        >
                          Unmatch
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
